import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices/DomainServicesContext";
import { datasetQueryKeys } from "@/shared/domain/datasets/hooks";
import { eventQueryKeys } from "@/shared/domain/events/hooks";
import { fileQueryKeys } from "@/shared/domain/files/hooks";

import { CollectionRecord, CollectionResourceRef } from "../CollectionRecord";

import { collectionQueryKeys } from "./collectionQueryKeys";

interface AddToCollectionsVariables {
  collectionIds: string[];
  resourceOwnerId: string;
  resources: CollectionResourceRef[];
}

export interface AddToCollectionsResult {
  succeeded: CollectionRecord[];
  failed: Array<{ collectionId: string; error: Error }>;
}

export function useAddToCollections() {
  const { collections } = useDomainServices();
  const queryClient = useQueryClient();

  return useMutation<AddToCollectionsResult, Error, AddToCollectionsVariables>({
    mutationFn: async ({ collectionIds, resourceOwnerId, resources }) => {
      const settled = await Promise.allSettled(
        collectionIds.map((collectionId) =>
          collections.updateCollection(
            collectionId,
            { add_resources: resources },
            { resourceOwnerId },
          ),
        ),
      );

      const succeeded: CollectionRecord[] = [];
      const failed: Array<{ collectionId: string; error: Error }> = [];

      settled.forEach((r, i) => {
        if (r.status === "fulfilled") {
          succeeded.push(r.value);
        } else {
          failed.push({
            collectionId: collectionIds[i],
            error:
              r.reason instanceof Error
                ? r.reason
                : new Error(String(r.reason)),
          });
        }
      });

      if (succeeded.length === 0) {
        throw failed[0]?.error ?? new Error("Failed to add to any collection");
      }

      return { succeeded, failed };
    },
    onSuccess: ({ succeeded }) => {
      for (const record of succeeded) {
        queryClient.setQueryData(
          collectionQueryKeys.detail(record.collection_id),
          record,
        );
      }

      // All succeeded records share org_id and resource_type: the picker
      // filters collections by a single resource type, and resourceOwnerId
      // is passed once for the whole mutation.
      const { org_id: orgId, resource_type: resourceType } = succeeded[0];

      void queryClient.invalidateQueries({
        queryKey: collectionQueryKeys.lists(orgId),
      });

      if (resourceType === "dataset") {
        void queryClient.invalidateQueries({
          queryKey: datasetQueryKeys.lists(),
        });
      } else if (resourceType === "file") {
        for (const record of succeeded) {
          void queryClient.invalidateQueries({
            queryKey: fileQueryKeys.listsScope(record.org_id, {
              type: "collection",
              id: record.collection_id,
            }),
          });
        }
      } else if (resourceType === "event") {
        for (const record of succeeded) {
          void queryClient.invalidateQueries({
            queryKey: eventQueryKeys.listsScope(record.org_id, {
              type: "collection",
              id: record.collection_id,
            }),
          });
        }
      }
    },
  });
}
